import React, { useState, useEffect, useRef, forwardRef } from "react";
import PropTypes from "prop-types";
import I18n from "../../base/i18n.js.jsx";
import FormFields from "../../base/form/form_fields.js.jsx";
import { makeStyles } from '@material-ui/core/styles';
import { itemWithKeys } from "../../utils/utils.js";
import { ProfileModel } from "../../models/profile/profile_model.js";

const blockStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  nameDesc: {
    fontSize: "16px",
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grayText,
    margin: "23px 0px 44px",
    ['@media (max-width: 768px)']: {
      fontSize: "10px",
      fontWeight: theme.typography.fontWeightRegular,
      margin: "10px 0px",
    },
  },
  fieldsBlock: {
    marginBottom: "36px",
    ['@media (max-width: 768px)']: {
      marginBottom: "18px",
    },
  }
}));

/**
 * Change callback for PersonalInfoFields
 * @callback PersonalInfoFields~onChange
 * @param {object} item Changed personal info of user
 */

 /**
 * Scroll to element function for PersonalInfoFields
 * @callback PersonalInfoFields~scrollToElement
 * @param {HTMLElement} element Element to scroll
 */

/**
 * Display info about user's personal info
 * @name PersonalInfoFields
 * @param {object} props React component properties
 * @param {object} [props.item] Personal info of user from API
 * @param {PersonalInfoFields~onChange} [props.onChange] On change handler
 * @param {PersonalInfoFields~scrollToElement} [props.scrollToElement] Pass it, if you current scroll container is not window
 * @param {boolean} [props.readOnly = false] State of enabled fields in component
 * @param {object.<string, any>} [props.errorData] Information about errors in fields after saving personal info
 * @example

 * return (
 *   <PersonalInfoFields item = { user.personalInfo }
 *                      errorData = { this.state.personalInfo }
 *                      onChange = { (item) => {
 *                        // Some actions
 *                      } } />
 * )
*/

const PersonalInfoFields = forwardRef((props, ref) => {
  const classes = blockStyles();

  const [item, setItem] = useState(props.item || {});
  const [readOnly, setReadOnly] = useState(props.readOnly);
  const [errorData, setErrorData] = useState(props.errorData);

  const { onChange, scrollToElement } = props;

  const formFields = ProfileModel.formFields({
    item: item
  }).map((field) => {
    let errors = errorData;
    if(!errors) {
      errors = {};
    }
    const { fields: { hasUsTaxLiability, politicallyExposed } = {} } = errors;
    if(field.name === "hasUsTaxLiability" && item[field.name] === "yes" && !hasUsTaxLiability) {
      field.descriptionText = I18n.t("activerecord.errors.models.users/personal_info.attributes.has_us_tax_liability.should_not_be_yes");
    }
    if(field.name === "politicallyExposed" && item[field.name] === "yes" && !politicallyExposed) {
      field.descriptionText = I18n.t("activerecord.errors.models.users/personal_info.attributes.politically_exposed.should_not_be_yes");
    }
    let alteredField = { ...field };
    return alteredField;
  });


  let salutationFields = ["salutation", "title"];
  let salutationFieldsRef = useRef();

  let nameFields = item.salutation == 'firma' ? ["firstName", "lastName", "companyName"] : ["firstName", "lastName"];
  let nameFieldsRef = useRef();

  const birthFields = ["birthDate", "birthPlace"];
  const birthFieldsRef = useRef();

  const citizenshipFields = ["citizenship"];
  const citizenshipFieldsRef = useRef();

  const contactFields = ["phoneNumber", "mobilePhoneNumber", "email"];
  const contactFieldsRef = useRef();

  const addressFields = ["address", "taxId"];
  const addressFieldsRef = useRef();

  const allFieldsRefs = useRef();

  const allFields = [
    ...salutationFields,
    ...nameFields,
    ...birthFields,
    ...citizenshipFields,
    ...contactFields,
    ...addressFields
  ];

  const fieldsGroupRefs = [
    salutationFieldsRef,
    nameFieldsRef,
    birthFieldsRef,
    citizenshipFieldsRef,
    contactFieldsRef,
    addressFieldsRef
  ];

  useEffect(() => {
    allFieldsRefs.current = {};
    fieldsGroupRefs.forEach((fieldsGroup) => {
      const current = fieldsGroup.current || {};
      const refs = current.fieldRefs;
      if(refs) {
        allFieldsRefs.current = {
          ...allFieldsRefs.current,
          ...refs
        }
      }
    })
  }, []);

  useEffect(() => {
    setItem(props.item);
  }, [props.item]);

  useEffect(() => {
    setItem(props.item);
  }, [props.item]);


  useEffect(() => {
    const { errorData } = props;
    setErrorData(errorData)
    if(!errorData) {
      return;
    }

    let { fields: fieldsErrorData = {} } = errorData;

    formFields.forEach(field => {
      if(field.type !== "nestedModelsArray" && field.type !== "nestedModel") {
        return;
      }
      if(!errorData[field.name]) {
        return;
      }
      fieldsErrorData[field.name] = errorData[field.name];
    })

    let firstErrorFieldRef;
    allFields.forEach((fieldName) => {
      if(firstErrorFieldRef) {
        return;
      }
      let fieldErrorInfo = fieldsErrorData[fieldName];
      let fieldRef       = allFieldsRefs.current[fieldName];

      if(fieldErrorInfo && fieldRef) {
        firstErrorFieldRef = fieldRef
        if(scrollToElement) {
          scrollToElement(firstErrorFieldRef);
        }
        else {
          window.scrollTo({
            top: firstErrorFieldRef.offsetTop,
            behavior: "smooth"
          });
        }

      }
    })
    if(!firstErrorFieldRef) {
      return;
    }
  }, [props.errorData]);

  useEffect(() => {
    setReadOnly(props.readOnly);
  }, [props.readOnly]);

  const onItemChange = (changedItem) => {
    const newItem = {
      ...item,
      ...changedItem
    };
    setItem(newItem);
    if(onChange) {
      onChange(newItem)
    }
  }

  return(
    <div className = { classes.root } >
      <div className = { classes.fieldsBlock }>
        <FormFields formFields = { formFields.filter(field => salutationFields.includes(field.name)) }
                    item = { itemWithKeys(item, salutationFields) }
                    errorData = { errorData }
                    onItemChange = { onItemChange }
                    readOnly = { readOnly }
                    ref = { salutationFieldsRef }/>
      </div>
      <div className = { classes.fieldsBlock }>
        <FormFields formFields = { formFields.filter(field => nameFields.includes(field.name)) }
                    item = { itemWithKeys(item, nameFields) }
                    errorData = { errorData }
                    onItemChange = { onItemChange }
                    readOnly = { readOnly }
                    ref = { nameFieldsRef }/>
        <div className = { classes.nameDesc }>
          { I18n.t("component.personal_info_fields.if_you_change_name") }
        </div>
      </div>
      <div className = { classes.fieldsBlock }>
        <FormFields formFields = { formFields.filter(field => birthFields.includes(field.name)) }
                    item = { itemWithKeys(item, birthFields) }
                    errorData = { errorData }
                    onItemChange = { onItemChange }
                    readOnly = { readOnly }
                    ref = { birthFieldsRef }/>
      </div>
      <div className = { classes.fieldsBlock }>
        <FormFields formFields = { formFields.filter(field => citizenshipFields.includes(field.name)) }
                    item = { itemWithKeys(item, citizenshipFields) }
                    errorData = { errorData }
                    onItemChange = { onItemChange }
                    readOnly = { readOnly }
                    ref = { citizenshipFieldsRef }/>
      </div>
      <div className = { classes.fieldsBlock }>
        <FormFields formFields = { formFields.filter(field => contactFields.includes(field.name)) }
                    item = { itemWithKeys(item, contactFields) }
                    errorData = { errorData }
                    onItemChange = { onItemChange }
                    readOnly = { readOnly }
                    ref = { contactFieldsRef }/>
      </div>
      <div className = { classes.fieldsBlock }>
        <FormFields formFields = { formFields.filter(field => addressFields.includes(field.name)) }
                    item = { itemWithKeys(item, addressFields) }
                    errorData = { errorData }
                    onItemChange = { onItemChange }
                    readOnly = { readOnly }
                    ref = { addressFieldsRef }/>
        <div className = { classes.nameDesc }>
          { I18n.t("component.personal_info_fields.tax_id") }
        </div>
      </div>
    </div>
  )
});

PersonalInfoFields.propTypes = {
  item: PropTypes.object,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  errorData: PropTypes.object,
  scrollToElement: PropTypes.func
}

PersonalInfoFields.defaultProps = {
  readOnly: false
}

export default PersonalInfoFields;
